import { ChangeEvent, cloneElement, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import {
    Grid,
    GridColumn,
    GridColumnProps,
    GridCustomCellProps,
    GridCustomHeaderCellProps,
    GridHeaderSelectionChangeEvent,
    GridKeyDownEvent,
    GridRowProps,
    GridSelectionChangeEvent,
    getSelectedState,
    GridDataStateChangeEvent,
    GridColumnMenuProps,
    GridColumnMenuFilter,
} from '@progress/kendo-react-grid';
import { classNames, getter, IconsContext } from '@progress/kendo-react-common';
import { getSelectedStateFromKeyDown, TableSelectableMode } from '@progress/kendo-react-data-tools';
import { CustomGridCell } from 'components';
import {
    process,
    State,
} from '@progress/kendo-data-query';
import { moreVerticalIcon, filterIcon } from '@progress/kendo-svg-icons';
import { filterOperators } from 'components/GridviewFilters/filterOperators'

export type GridViewProps = {
    className?: string,
    cellClassName?: string,
    data: any[],
    dataItemKey: string,
    gridColumns: GridColumnProps[],
    isReadOnly: boolean,
    selectMode: TableSelectableMode,
    onSelectedItemsChange: (event: GridViewSelectionChangeEvent) => void,
    pageSize?: number,
    selectedItems?: any[],
    selectionGroupField?: string
};

export type GridViewSelectionChangeEvent = {
    selectedItems: any[],
    changedItem?: any,
    selectionGroupValue?: string
};

const ColumnMenu = (props: GridColumnMenuProps) => {
    return (
        <div>
            <GridColumnMenuFilter {...props} expanded={true} />
        </div>
    );
};

const SELECTED_FIELD = 'selected';

export const GridView = (props: GridViewProps) => {
    const {
        className,
        cellClassName,
        data,
        dataItemKey,
        gridColumns,
        isReadOnly,
        onSelectedItemsChange,
        selectMode,
        pageSize = 15,
        selectedItems: controlledSelectedItems,
        selectionGroupField
    } = props;

    const [selectedState, setSelectedState] = useState<{
        [id: string]: boolean | number[];
    }>({});
    const isMultiSelect = selectMode === 'multiple';
    const idGetter = useMemo(() => getter(dataItemKey), [dataItemKey]);

    const [dataState, setDataState] = useState<State>({
        take: pageSize,
        skip: 0,
        sort: [{ field: 'ProductName', dir: 'asc' }],
        filter: {
            logic: 'and',
            filters: []
        }
    });

    const keyedData = useMemo(() => data.map((item, index) => ({
        ...item,
        [dataItemKey]: item[dataItemKey] ?? `generated-${index}`
    })), [data, dataItemKey]);

    const getSelectionGroupValue = useCallback((item: any) => {
        if (!selectionGroupField) {
            return undefined;
        }

        const rawValue = item?.[selectionGroupField];
        const normalized = rawValue === undefined || rawValue === null
            ? ''
            : rawValue.toString().trim();

        return normalized || `__missing_${idGetter(item)}`;
    }, [idGetter, selectionGroupField]);

    const getSelectedItemsFromState = useCallback((newSelectedState: any) => {
        return keyedData.filter((item: any) => {
            return newSelectedState[idGetter(item)];
        });
    }, [idGetter, keyedData]);

    const selectedItems = useMemo(
        () => getSelectedItemsFromState(selectedState),
        [getSelectedItemsFromState, selectedState]
    );

    const selectedGroupValue = useMemo(
        () => selectedItems.length ? getSelectionGroupValue(selectedItems[0]) : undefined,
        [getSelectionGroupValue, selectedItems]
    );

    const findDataItem = useCallback((item: any) => {
        const itemId = idGetter(item);
        return keyedData.find((dataItem: any) => idGetter(dataItem) === itemId) ?? item;
    }, [idGetter, keyedData]);

    useEffect(() => {
        if (!controlledSelectedItems) {
            return;
        }

        const nextSelectedState = controlledSelectedItems.reduce((state: any, item: any) => {
            state[idGetter(item)] = true;
            return state;
        }, {});

        const currentKeys = Object.keys(selectedState).filter(key => selectedState[key]);
        const nextKeys = Object.keys(nextSelectedState);
        const stateMatches = currentKeys.length === nextKeys.length
            && currentKeys.every(key => nextSelectedState[key]);

        if (!stateMatches) {
            setSelectedState(nextSelectedState);
        }
    }, [controlledSelectedItems, idGetter, selectedState]);

    const isSelectionGroupDisabled = useCallback((item: any) => {
        if (!selectionGroupField || !selectedGroupValue) {
            return false;
        }

        const itemId = idGetter(item);
        return !selectedState[itemId] && getSelectionGroupValue(item) !== selectedGroupValue;
    }, [getSelectionGroupValue, idGetter, selectedGroupValue, selectedState, selectionGroupField]);

    const sanitizeSelectedState = useCallback((newSelectedState: any, changedItem?: any) => {
        if (!selectionGroupField) {
            return newSelectedState;
        }

        const changedDataItem = changedItem ? findDataItem(changedItem) : undefined;

        if (changedDataItem && isSelectionGroupDisabled(changedDataItem)) {
            return selectedState;
        }

        const newSelectedItems = getSelectedItemsFromState(newSelectedState);
        if (!newSelectedItems.length) {
            return {};
        }

        const newGroupValue = selectedGroupValue && newSelectedItems.some((item: any) => getSelectionGroupValue(item) === selectedGroupValue)
            ? selectedGroupValue
            : getSelectionGroupValue(changedDataItem ?? newSelectedItems[0]);

        return newSelectedItems.reduce((state: any, item: any) => {
            if (getSelectionGroupValue(item) === newGroupValue) {
                state[idGetter(item)] = true;
            }
            return state;
        }, {});
    }, [
        findDataItem,
        getSelectedItemsFromState,
        getSelectionGroupValue,
        idGetter,
        isSelectionGroupDisabled,
        selectedGroupValue,
        selectedState,
        selectionGroupField
    ]);

    const onSelectedItemsChanged = useCallback((newSelectedState: any, changedItem?: any) => {
        if (onSelectedItemsChange) {
            const selectedItems = getSelectedItemsFromState(newSelectedState);
            const changedDataItem = changedItem ? findDataItem(changedItem) : undefined;
            onSelectedItemsChange({
                selectedItems,
                changedItem: changedDataItem,
                selectionGroupValue: selectedItems.length ? getSelectionGroupValue(selectedItems[0]) : undefined
            });
        }
    }, [
        findDataItem,
        getSelectedItemsFromState,
        getSelectionGroupValue,
        onSelectedItemsChange
    ]);

    const onHeaderSelectionChange = useCallback(
        (event: GridHeaderSelectionChangeEvent) => {
            if (selectionGroupField && !selectedGroupValue) {
                return;
            }

            const checkboxElement: any = event.syntheticEvent.target;
            const checked = checkboxElement.checked;
            const newSelectedState: any = { ...selectedState };
            event.dataItems.forEach((item) => {
                const dataItem = findDataItem(item);
                const canSelect = !selectionGroupField || getSelectionGroupValue(dataItem) === selectedGroupValue;

                if (canSelect && checked) {
                    newSelectedState[idGetter(dataItem)] = true;
                } else if (canSelect) {
                    delete newSelectedState[idGetter(dataItem)];
                }
            });
            const sanitizedSelectedState = sanitizeSelectedState(newSelectedState);
            setSelectedState(sanitizedSelectedState);
            onSelectedItemsChanged(sanitizedSelectedState);
        },
        [
            findDataItem,
            getSelectionGroupValue,
            idGetter,
            onSelectedItemsChanged,
            sanitizeSelectedState,
            selectedGroupValue,
            selectedState,
            selectionGroupField
        ]
    );

    const onKeyDown = useCallback((event: GridKeyDownEvent) => {
        const keyPressed: string = event.nativeEvent.code;
        if (keyPressed === 'Space' || keyPressed === 'Enter') {
            const newSelectedState = getSelectedStateFromKeyDown({
                event,
                selectedState,
                dataItemKey,
            });
            const sanitizedSelectedState = sanitizeSelectedState(newSelectedState);
            setSelectedState(sanitizedSelectedState);
            onSelectedItemsChanged(sanitizedSelectedState);
        }
    }, [dataItemKey, onSelectedItemsChanged, sanitizeSelectedState, selectedState]);

    const onSelectionChange = useCallback(
        (event: GridSelectionChangeEvent) => {
            event.mode = selectMode;

            const textSelection = document.getSelection()?.toString();
            const isTextSelected = !!textSelection;

            if ((isMultiSelect && event.startColIndex === 0) || !isTextSelected) {
                if (event.dataItem && isSelectionGroupDisabled(findDataItem(event.dataItem))) {
                    return;
                }

                const newSelectedState = getSelectedState({
                    event,
                    selectedState,
                    dataItemKey
                });
                const sanitizedSelectedState = sanitizeSelectedState(newSelectedState, event.dataItem);
                setSelectedState(sanitizedSelectedState);
                onSelectedItemsChanged(sanitizedSelectedState, event.dataItem);
            }
        },
        [
            dataItemKey,
            findDataItem,
            isMultiSelect,
            isSelectionGroupDisabled,
            onSelectedItemsChanged,
            sanitizeSelectedState,
            selectMode,
            selectedState
        ]
    );

    const dataStateChange = useCallback((event: GridDataStateChangeEvent) => {
        setDataState(event.dataState);
    }, []);

    const gridData = useMemo(() => keyedData.map(item => ({
        ...item,
        [SELECTED_FIELD]: selectedState[idGetter(item)]
    })), [keyedData, idGetter, selectedState]);

    const processedData = useMemo(() => {
        return process(gridData, dataState);
    }, [gridData, dataState]);

    const headerSelectionValue = useMemo(() => {
        const selectableItems = selectionGroupField && selectedGroupValue
            ? processedData.data.filter((item: any) => getSelectionGroupValue(item) === selectedGroupValue)
            : processedData.data;

        return selectableItems.length > 0 && selectableItems.every((item: any) => selectedState[idGetter(item)]);
    }, [
        getSelectionGroupValue,
        idGetter,
        processedData.data,
        selectedGroupValue,
        selectedState,
        selectionGroupField
    ]);

    const isHeaderSelectionDisabled = !!selectionGroupField && !selectedGroupValue;

    const SelectionCell = useCallback((cellProps: GridCustomCellProps & { tdProps?: any, selectionChange?: (event: any) => void }) => {
        const dataItem = findDataItem(cellProps.dataItem);
        const disabled = isSelectionGroupDisabled(dataItem);
        const checked = !!selectedState[idGetter(dataItem)];
        const tdProps = cellProps.tdProps ?? {};

        const onChange = (event: ChangeEvent<HTMLInputElement>) => {
            if (disabled) {
                event.preventDefault();
                event.stopPropagation();
                return;
            }

            cellProps.selectionChange && cellProps.selectionChange({ syntheticEvent: event });
        };

        return (
            <td
                {...tdProps}
                aria-disabled={disabled || undefined}
                className={classNames(tdProps.className, {
                    'adjustments-gridview-grid-selection-disabled': disabled
                })}
            >
                <span className="k-checkbox-wrap">
                    <input
                        aria-label="Select row"
                        aria-checked={checked}
                        checked={checked}
                        className="k-checkbox k-checkbox-md k-rounded-md"
                        disabled={disabled}
                        onChange={onChange}
                        tabIndex={-1}
                        type="checkbox"
                    />
                </span>
            </td>
        );
    }, [findDataItem, idGetter, isSelectionGroupDisabled, selectedState]);

    const HeaderSelectionCell = useCallback((headerProps: GridCustomHeaderCellProps & {
        field?: string,
        selectionChange?: (event: any) => void,
        selectionValue?: boolean
    }) => {
        const {
            ariaColumnIndex,
            ariaDescription,
            ariaSelected,
            ariaSort,
            className,
            columnId,
            navigatable,
            ...thProps
        } = headerProps.thProps as any ?? {};
        const onChange = (event: ChangeEvent<HTMLInputElement>) => {
            if (isHeaderSelectionDisabled) {
                event.preventDefault();
                event.stopPropagation();
                return;
            }

            headerProps.selectionChange && headerProps.selectionChange({
                field: headerProps.field,
                syntheticEvent: event
            });
        };

        return (
            <th
                {...thProps}
                aria-colindex={ariaColumnIndex}
                aria-description={ariaDescription}
                aria-selected={ariaSelected}
                aria-sort={ariaSort}
                data-columnid={columnId}
                className={classNames(className, {
                    'adjustments-gridview-grid-selection-disabled': isHeaderSelectionDisabled
                })}
            >
                <span className="k-checkbox-wrap">
                    <input
                        aria-label="Select rows"
                        aria-checked={headerSelectionValue}
                        checked={headerSelectionValue}
                        className="k-checkbox k-checkbox-md k-rounded-md"
                        disabled={isHeaderSelectionDisabled}
                        onChange={onChange}
                        tabIndex={-1}
                        type="checkbox"
                    />
                </span>
            </th>
        );
    }, [headerSelectionValue, isHeaderSelectionDisabled]);

    const rowRender = useCallback((row: ReactElement, rowProps: GridRowProps) => {
        const disabled = rowProps.rowType === 'data' && isSelectionGroupDisabled(findDataItem(rowProps.dataItem));

        return cloneElement(row, {
            'aria-disabled': disabled || undefined,
            className: classNames(row.props.className, {
                'adjustments-gridview-grid-row-disabled': disabled
            })
        });
    }, [findDataItem, isSelectionGroupDisabled]);

    const processedColumns = useMemo(() => {
        return gridColumns.map(column => {
            if (!column.field || column.field === SELECTED_FIELD) {
                return column;
            }

            let filterType: 'text' | 'numeric' | 'date' | 'boolean' = 'text';

            if (column.filterTitle === 'numeric') {
                filterType = 'numeric';
            } else if (column.filterTitle === 'date') {
                filterType = 'date';
            } else if (column.filterTitle === 'boolean') {
                filterType = 'boolean';
            }

            return {
                ...column,
                filter: filterType,
                columnMenu: ColumnMenu,
            };
        });
    }, [gridColumns]);

    const gridProps = {
        className,
        data: processedData.data,
        dataItemKey,
        navigatable: true,
        onHeaderSelectionChange,
        onKeyDown,
        onSelectionChange,
        selectable: {
            enabled: !isReadOnly,
            cell: false,
            mode: selectMode
        },
        selectedField: SELECTED_FIELD,
        sortable: true,
        sort: dataState.sort,
        pageable: true,
        pageSize,
        total: processedData.total,
        skip: dataState.skip,
        take: dataState.take,
        onDataStateChange: dataStateChange,
        filter: dataState.filter,
        filterOperators: filterOperators,
        rowRender,
    };

    return (
        <IconsContext.Provider
            value={{
                type: 'svg',
                icons: { [moreVerticalIcon.name]: filterIcon },
            }}
        >
            <Grid {...gridProps} >
                {isReadOnly === false && isMultiSelect &&
                    <GridColumn
                        field={SELECTED_FIELD}
                        className={cellClassName}
                        key={SELECTED_FIELD}
                        headerSelectionValue={headerSelectionValue}
                        width="50px"
                        filterable={false}
                        sortable={false}
                        cells={{
                            headerCell: HeaderSelectionCell,
                            select: {
                                data: SelectionCell
                            }
                        }}
                    />
                }
                {processedColumns.map(columnProps => (
                    <GridColumn
                        className={cellClassName}
                        key={columnProps.field ?? Math.random().toString(16).slice(2)}
                        {...columnProps}
                        cell={columnProps.cell ?? CustomGridCell}
                        width={undefined}
                    />
                ))}
            </Grid>
        </IconsContext.Provider>
    );
};
